import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';

export const GROW_DASHBOARD_KEY = 'growDashboard';

export interface AppliedCustomerFilters {
  status: string;
  tagIds: string[];
  teamMemberIds: string[];
  cfaIds: string[];
  assignedMemberIds: string[];
  groupById?: string;
  queryType?: string[];
}

export interface GrowDashboardFunnelFilterState {
  appliedFilters: AppliedCustomerFilters;
  searchText: string;
}

export interface GrowDashboardState {
  filtersByFunnelId: {
    [funnelId: string]: GrowDashboardFunnelFilterState;
  };
}

const defaultFunnelFilterState: GrowDashboardFunnelFilterState = {
  appliedFilters: {
    status: 'all',
    tagIds: [],
    teamMemberIds: [],
    cfaIds: [],
    assignedMemberIds: [],
    groupById: 'default',
    queryType: [],
  },
  searchText: '',
};

export const initialGrowDashboardState: GrowDashboardState = {
  filtersByFunnelId: {},
};

export const growDashboardSlice = createSlice({
  name: GROW_DASHBOARD_KEY,
  initialState: initialGrowDashboardState,
  reducers: {
    setFunnelAppliedFilters(
      state,
      action: PayloadAction<{
        funnelId: string;
        appliedFilters: AppliedCustomerFilters;
      }>
    ) {
      const { funnelId, appliedFilters } = action.payload;
      if (!state.filtersByFunnelId[funnelId]) {
        state.filtersByFunnelId[funnelId] = { ...defaultFunnelFilterState };
      }
      state.filtersByFunnelId[funnelId].appliedFilters = appliedFilters;
    },
    setFunnelSearchText(
      state,
      action: PayloadAction<{ funnelId: string; searchText: string }>
    ) {
      const { funnelId, searchText } = action.payload;
      if (!state.filtersByFunnelId[funnelId]) {
        state.filtersByFunnelId[funnelId] = { ...defaultFunnelFilterState };
      }
      state.filtersByFunnelId[funnelId].searchText = searchText;
    },
  },
});

export const growDashboardReducer = growDashboardSlice.reducer;

export const { setFunnelAppliedFilters, setFunnelSearchText } =
  growDashboardSlice.actions;

export const getGrowDashboardSliceState = (
  rootState: RootState
): GrowDashboardState => rootState[GROW_DASHBOARD_KEY];

export const makeGetFunnelFilters = (funnelId: string) =>
  createSelector(getGrowDashboardSliceState, (state) => {
    return state.filtersByFunnelId[funnelId] ?? defaultFunnelFilterState;
  });
